import { addDays, endOfDay, isAfter } from 'date-fns';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { PlannedOccurrence } from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export const useDueStatusGroupedTasks = (tasks: Task[]) => {
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  return useMemo(() => {
    const overdueDeadline = endOfDay(addDays(today, -1));

    return tasks.reduce<{ todayTasks: Task[]; overdueTasks: Task[] }>(
      (acc, task) => {
        const planning = getDueTaskPlanning(task, {
          date: overdueDeadline,
          today,
          weekStartsOn,
        });

        const duePlanning = planning.filter(
          (plannedOccurrence) =>
            !isAfter(plannedOccurrence.date, overdueDeadline),
        );

        const plannedOccurrence: PlannedOccurrence | undefined =
          duePlanning[duePlanning.length - 1];

        if (
          plannedOccurrence ||
          (task.deadlineDeadline &&
            !isAfter(task.deadlineDeadline, overdueDeadline))
        ) {
          acc.overdueTasks.push(task);
          return acc;
        }

        acc.todayTasks.push(task);
        return acc;
      },
      { todayTasks: [], overdueTasks: [] },
    );
  }, [tasks, today, weekStartsOn]);
};
